import React, { useEffect, useMemo, useState } from 'react';
import useHead from '../useHead';
import AdminLogin from './AdminLogin';
import AdminNav from './AdminNav';
import { adminFetch, useAdminToken } from './adminAuth';

function ConversationList({ token }) {
  const [conversations, setConversations] = useState(null);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [search, setSearch] = useState('');

  useHead({
    title: 'Admin — Conversations — ConvoLines',
    description: '',
    path: '/admin/conversations',
    noindex: true,
  });

  useEffect(() => {
    adminFetch(token, '/api/v1/admin/conversations')
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load conversations');
        return res.json();
      })
      .then(setConversations)
      .catch(() => setError('Failed to load conversations'));
  }, [token]);

  async function toggleFree(conversation) {
    setBusyId(conversation.id);
    try {
      const res = await adminFetch(token, `/api/v1/admin/conversations/${conversation.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_free: !conversation.is_free }),
      });
      if (!res.ok) throw new Error('Failed to update conversation');
      const updated = await res.json();
      setConversations((list) => list.map((item) => (item.id === updated.id ? updated : item)));
    } catch (err) {
      window.alert(err.message);
    } finally {
      setBusyId(null);
    }
  }

  // Title, situation and category are all worth searching — a title alone rarely
  // says which situation it belongs to.
  const matches = useMemo(() => {
    if (!conversations) return [];
    const query = search.trim().toLowerCase();
    if (!query) return conversations;
    return conversations.filter((conversation) =>
      `${conversation.title} ${conversation.situation} ${conversation.category}`
        .toLowerCase()
        .includes(query)
    );
  }, [conversations, search]);

  if (error) return <p className="admin-error">{error}</p>;
  if (!conversations) return <p>Loading…</p>;

  const premiumCount = conversations.filter((conversation) => !conversation.is_free).length;

  return (
    <>
      <h1>Conversations ({conversations.length})</h1>
      <p className="admin-muted">{premiumCount} premium · {conversations.length - premiumCount} free</p>
      <input
        type="search"
        className="admin-search"
        placeholder="Search title, situation or category…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        autoFocus
      />
      <table>
        <thead>
          <tr>
            <th>Title</th>
            <th>Situation</th>
            <th>Access</th>
          </tr>
        </thead>
        <tbody>
          {matches.map((conversation) => (
            <tr key={conversation.id}>
              <td>
                {conversation.title}
                {conversation.status !== 'published' && (
                  <span className="admin-badge admin-badge--inactive">{conversation.status}</span>
                )}
              </td>
              <td className="admin-muted">
                {conversation.category} · {conversation.situation}
              </td>
              <td>
                <div className="admin-actions">
                  <span
                    className={`admin-badge ${conversation.is_free ? 'admin-badge--active' : 'admin-badge--inactive'}`}
                  >
                    {conversation.is_free ? 'free' : 'premium'}
                  </span>
                  <button
                    className="admin-action-btn"
                    disabled={busyId === conversation.id}
                    onClick={() => toggleFree(conversation)}
                  >
                    {busyId === conversation.id
                      ? 'Working…'
                      : conversation.is_free
                        ? 'Make premium'
                        : 'Make free'}
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {matches.length === 0 && <p className="admin-muted">No conversations match “{search}”.</p>}
    </>
  );
}

export default function AdminConversations() {
  const [token, setToken] = useAdminToken();

  if (!token) return <AdminLogin onAuthed={setToken} />;

  return (
    <div className="admin-page">
      <AdminNav />
      <ConversationList token={token} />
    </div>
  );
}
